import React from 'react';
import { ArrowLeft, Wallet, MapPin, Flag, Article, HouseLine, Archive, Sparkle, Check } from '@phosphor-icons/react';
import { AnuntHistoryEntry, formatBudget, getOffers, StoredOffer } from '../../lib/utils';
import { HOUSING_TYPES } from '../../lib/constants';
interface AnuntSnapshotViewProps {
  entry: AnuntHistoryEntry;
  onBack: () => void;
}
function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('ro-RO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  } catch {
    return '';
  }
}
function groupLabel(status: StoredOffer['status']): string {
  switch (status) {
    case 'succes':
      return 'Închiriat';
    case 'saved':
      return 'Salvate';
    case 'postponed':
      return 'Amânate';
    case 'rejected':
      return 'Respinse';
    case 'new':
      return 'Necitite';
    case 'archived':
      return 'Arhivate';
  }
}
export function AnuntSnapshotView({
  entry,
  onBack
}: AnuntSnapshotViewProps) {
  const isSucces = entry.status === 'succes';
  const snap = entry.anuntSnapshot;
  const housingLabel = HOUSING_TYPES.find(t => t.value === snap.housingType)?.label || 'Apartament';
  const locationPills = [snap.rooms, snap.floor, snap.yearBuiltLabel].filter(Boolean) as string[];
  // Resolve associated offers from storage (they still exist with archived/succes status)
  const allOffers = getOffers();
  const associatedOffers = allOffers.filter(o => entry.associatedOfferIds.includes(o.id));
  // Group offers by their _preArchiveStatus (or 'succes' for the success offer)
  const grouped: Record<string, StoredOffer[]> = {};
  associatedOffers.forEach(o => {
    const key = o.status === 'succes' ? 'succes' : o._preArchiveStatus || o.status || 'archived';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(o);
  });
  const groupOrder: Array<StoredOffer['status']> = ['succes', 'saved', 'postponed', 'new', 'rejected', 'archived'];
  const closedAtLabel = formatDate(entry.closedAt);
  return <div className="space-y-6" data-id="element-144">
      {/* Header */}
      <div className="flex items-center gap-3" data-id="element-145">
        <button type="button" onClick={onBack} className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-dark-3 hover:bg-dark-4 border border-dark-4 text-light-2 transition-colors cursor-pointer" aria-label="Înapoi" data-id="element-146">
          <ArrowLeft size={18} weight="bold" data-id="element-147" />
        </button>
        <div className="min-w-0" data-id="element-148">
          <h1 className="text-xl sm:text-2xl font-bold text-light-1" data-id="element-149">
            {isSucces ? 'Sesiune închisă cu succes' : 'Anunț arhivat'}
          </h1>
          {closedAtLabel && <p className="text-xs text-light-3 mt-0.5" data-id="element-150">
              Închis pe {closedAtLabel}
            </p>}
        </div>
      </div>

      {/* Read-only banner */}
      <div className={'rounded-xl border px-4 py-3 flex items-center gap-3 ' + (isSucces ? 'bg-lime-5 border-lime-4' : 'bg-dark-2 border-dark-3')} data-id="element-151">
        {isSucces ? <Sparkle size={18} weight="fill" className="text-lime-1 shrink-0" data-id="element-152" /> : <Archive size={16} weight="regular" className="text-light-2 shrink-0" data-id="element-153" />}
        <p className="text-xs text-light-2" data-id="element-154">
          {isSucces && entry.succesCounterpartyName ? `Ai închiriat cu ${entry.succesCounterpartyName}. Această sesiune este doar pentru consultare.` : 'Această sesiune este doar pentru consultare. Datele sunt înghețate la momentul închiderii.'}
        </p>
      </div>

      {/* Frozen anunț details */}
      <article className="rounded-2xl bg-dark-2 border border-dark-3 overflow-hidden" data-id="element-155">
        <header className="px-6 py-4 border-b border-dark-3" data-id="element-156">
          <h2 className="text-base font-semibold text-light-1" data-id="element-157">
            Detalii anunț
          </h2>
        </header>
        <div className="px-6 py-7 space-y-7" data-id="element-158">
          <ul className="space-y-3 text-sm text-light-2" data-id="element-159">
            {snap.budgetMaxEur > 0 && <li className="flex items-center gap-3" data-id="element-160">
                <Wallet size={18} weight="regular" className="text-light-2 shrink-0" data-id="element-161" />
                <span data-id="element-162">max {formatBudget(snap.budgetMaxEur)}</span>
              </li>}
            {snap.neighborhoods && <li className="flex items-center gap-3" data-id="element-163">
                <MapPin size={18} weight="regular" className="text-light-2 shrink-0" data-id="element-164" />
                <span data-id="element-165">{snap.neighborhoods}</span>
              </li>}
            {(snap.moveInDate || snap.contractDuration) && <li className="flex items-center gap-3" data-id="element-166">
                <Flag size={18} weight="regular" className="text-light-2 shrink-0" data-id="element-167" />
                <span data-id="element-168">
                  {snap.moveInDate}
                  {snap.contractDuration ? ` · ${snap.contractDuration}` : ''}
                </span>
              </li>}
            {snap.livingMode && <li className="flex items-center gap-3" data-id="element-169">
                <Article size={18} weight="regular" className="text-light-2 shrink-0" data-id="element-170" />
                <span data-id="element-171">
                  {[snap.livingMode, snap.isSmoker ? 'Fumător' : 'Nefumători', snap.pets].filter(Boolean).join(' · ')}
                </span>
              </li>}
          </ul>

          {(snap.rooms || snap.floor || snap.yearBuiltLabel) && <div data-id="element-172">
              <h3 className="text-xs font-medium text-light-3 uppercase tracking-wider mb-3" data-id="element-173">
                Despre loc
              </h3>
              <div className="flex flex-wrap gap-2" data-id="element-174">
                <span className="inline-flex items-center h-10 px-4 rounded-full text-sm font-normal bg-dark-4 text-light-1 border border-dark-4" data-id="element-175">
                  {housingLabel}
                </span>
                {locationPills.map(p => <span key={p} className="inline-flex items-center h-10 px-4 rounded-full text-sm font-normal border border-dark-4 text-light-2" data-id="element-176">
                    {p}
                  </span>)}
              </div>
            </div>}

          {snap.amenities && snap.amenities.length > 0 && <div data-id="element-177">
              <h3 className="text-xs font-medium text-light-3 uppercase tracking-wider mb-3" data-id="element-178">
                Facilități dorite
              </h3>
              <div className="flex flex-wrap gap-2" data-id="element-179">
                {snap.amenities.map(a => <span key={a} className="inline-flex items-center h-10 px-4 rounded-full text-sm font-normal border border-dark-4 text-light-2" data-id="element-180">
                    {a}
                  </span>)}
              </div>
            </div>}

          {snap.searchBio && <div data-id="element-181">
              <h3 className="text-xs font-medium text-light-3 uppercase tracking-wider mb-3" data-id="element-182">
                Ce caut
              </h3>
              <p className="text-sm text-light-2 leading-relaxed" data-id="element-183">
                {snap.searchBio}
              </p>
            </div>}
        </div>
      </article>

      {/* Frozen offers grouped by pre-archive status */}
      {associatedOffers.length > 0 && <article className="rounded-2xl bg-dark-2 border border-dark-3 overflow-hidden" data-id="element-184">
          <header className="px-6 py-4 border-b border-dark-3" data-id="element-185">
            <h2 className="text-base font-semibold text-light-1" data-id="element-186">
              Oferte din această sesiune
            </h2>
            <p className="text-xs text-light-3 mt-0.5" data-id="element-187">
              {associatedOffers.length} oferte · înghețate la momentul
              închiderii
            </p>
          </header>
          <div className="px-6 py-5 space-y-6" data-id="element-188">
            {groupOrder.map(status => {
          const offers = grouped[status];
          if (!offers || offers.length === 0) return null;
          return <div key={status} data-id="element-189">
                  <h3 className="text-xs font-medium text-light-3 uppercase tracking-wider mb-3" data-id="element-190">
                    {groupLabel(status)} · {offers.length}
                  </h3>
                  <div className="space-y-2" data-id="element-191">
                    {offers.map(o => {
                const isSuccesOffer = o.status === 'succes';
                return <div key={o.id} className={'rounded-xl p-3 flex items-center gap-3 ' + (isSuccesOffer ? 'bg-lime-5 border border-lime-4' : 'bg-dark-1 border border-dark-3')} data-id="element-192">
                          {o.fromAvatar ? <img src={o.fromAvatar} alt={o.fromName} className="w-10 h-10 rounded-full object-cover shrink-0" data-id="element-193" /> : <div className="w-10 h-10 rounded-full bg-dark-3 flex items-center justify-center text-light-2 text-sm font-medium shrink-0" data-id="element-194">
                              {o.fromName.charAt(0)}
                            </div>}
                          <div className="min-w-0 flex-1" data-id="element-195">
                            <div className="flex items-center gap-2" data-id="element-196">
                              <p className="text-sm font-medium text-light-1 truncate" data-id="element-197">
                                {o.fromName}
                              </p>
                              {isSuccesOffer && <Check size={14} weight="bold" className="text-lime-1 shrink-0" data-id="element-198" />}
                            </div>
                            {o.listingPreview?.title && <p className="text-xs text-light-3 truncate mt-0.5" data-id="element-199">
                                {o.listingPreview.title}
                              </p>}
                          </div>
                          {o.listingPreview?.priceLabel && <span className="text-xs text-light-2 shrink-0" data-id="element-200">
                              {o.listingPreview.priceLabel}
                            </span>}
                        </div>;
              })}
                  </div>
                </div>;
        })}
          </div>
        </article>}
    </div>;
}